import { useCallback, useState, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useTicker } from 'tradera-hooks/use-ticker';
import { syncServerTime } from 'tradera-utils/server-date';
import { useTranslator } from 'tradera-lang/translate';
import { selectPreferredLanguageCode } from 'tradera-state/language/selectors';
import { useDayJs } from 'tradera-hooks/use-day-js';

const COUNTDOWN_TICK_INTERVAL = 1000;
const COUNTDOWN_DEFAULT_WARNING_LIMIT = 12 * 60 * 60;

/**
 *
 * @return {{timeLeftText: string, isCountdownEnabled: boolean, isCountdownDone: boolean, isTimeLeftBelowWarningLimit: boolean}}
 * isCountdownEnabled - True if client time is synched with server time.
 * isCountdownDone - True if there is no time left after counting down.
 * timeLeftText - The time left, formatted by the given formatter function.
 * isTimeLeftBelowWarningLimit - True if time is below the given warningLimit.
 */
export const useCountdown = (
    endDateStr,
    formatTimeLeft,
    warningLimit = COUNTDOWN_DEFAULT_WARNING_LIMIT
) => {
    const { t } = useTranslator();
    const [isCountdownDone, setIsCountdownDone] = useState(false);
    const [timeLeftText, setTimeLeftText] = useState('');
    const [isCountdownEnabled, setIsCountdownEnabled] = useState(false);
    const [
        isTimeLeftBelowWarningLimit,
        setIsTimeLeftBelowWarningLimit
    ] = useState(false);
    const previousAdjustment = useRef(0);
    const endDate = useMemo(() => new Date(endDateStr), [endDateStr]);
    const preferredLanguageCode = useSelector(selectPreferredLanguageCode);
    const dayjs = useDayJs();

    const updateTimeLeft = useCallback(
        async (isInitialCall = false) => {
            const serverDate = await syncServerTime(preferredLanguageCode);
            if (!serverDate) {
                // In case server date is lost we need to
                // ensure everything looks OK.
                setIsCountdownEnabled(false);
                setIsCountdownDone(false);
                setTimeLeftText('');
                setIsTimeLeftBelowWarningLimit(false);
                return;
            }
            setIsCountdownEnabled(true);

            const secondsLeftWithFractions = (endDate - serverDate) / 1000;
            let adjustedSecondsLeftWithFractions =
                secondsLeftWithFractions - previousAdjustment.current;
            let secondsLeft = Math.round(adjustedSecondsLeftWithFractions);

            // The amount we adjusted the time at this tick.
            // We use this at the next tick to make adjustedSecondsLeftWithFractions
            // as close to whole seconds as possible.
            // In other words adjustedSecondsLeftWithFractions - secondsLeft should be as close to zero
            // as possible.
            let adjustment = secondsLeftWithFractions - secondsLeft;

            // Don't let the adjustment to drift over a second up or down.
            if (adjustment >= 1 || adjustment <= -1) {
                // Reset the adjustment
                secondsLeft = Math.round(secondsLeftWithFractions);
                adjustment = secondsLeftWithFractions - secondsLeft;
            }

            // The first time here we are not in a tick so
            // ignore adjustment.
            if (!isInitialCall) {
                previousAdjustment.current = adjustment;
            }

            const formattedTimeLeft =
                secondsLeft > 0
                    ? formatTimeLeft(secondsLeft, t, endDate, serverDate, dayjs)
                    : t('vip_ended');

            setTimeLeftText(formattedTimeLeft);
            setIsTimeLeftBelowWarningLimit(secondsLeft <= warningLimit);

            if (secondsLeft <= 0) {
                setIsCountdownDone(true);
            }
        },
        [endDate, formatTimeLeft, t, warningLimit, preferredLanguageCode, dayjs]
    );

    useEffect(() => {
        updateTimeLeft(true);
    }, [updateTimeLeft]);

    useTicker(updateTimeLeft, isCountdownDone ? 0 : COUNTDOWN_TICK_INTERVAL);

    return {
        timeLeftText,
        isCountdownDone,
        isCountdownEnabled,
        isTimeLeftBelowWarningLimit
    };
};
